/**
 * kdsLoadAlerts.js — Kitchen-load alerting engine for the KDS Telegram alerts.
 *
 * Design follows standard ops-alerting practice (Prometheus Alertmanager,
 * PagerDuty, Google SRE "Monitoring Distributed Systems"):
 *   - Symptom-based: alert when the kitchen is actually BEHIND (delays, backlog,
 *     oldest age), not merely busy.
 *   - Edge-triggered: notify on a level CHANGE, never on a bare timer.
 *   - Sustain ("for:") window: a level must hold briefly before it fires, so a
 *     short burst that staff clear immediately doesn't page anyone.
 *   - Hysteresis: separate enter/clear thresholds create a deadband so the level
 *     can't flap on and off at the boundary (Datadog "recovery threshold").
 *   - Renotify / heartbeat: while CRITICAL, re-ping on a long interval
 *     (Alertmanager `repeat_interval`).
 *   - Auto-resolve: one "caught up" message when it returns to OK — but only if
 *     we actually notified during the incident (Alertmanager `send_resolved`).
 *   - Seeding: on open / store reopen the current level is adopted SILENTLY, so
 *     opening mid-rush never fires a fresh alert; it only speaks up on a real
 *     escalation (or a heartbeat if it stays critical).
 *
 * All functions are pure. KdsBoardTab holds the returned state in a ref and does
 * the actual network send.
 */

import { computeActiveElapsed } from './ticketTimingUtils';

export const LEVELS = { ok: 0, warning: 1, critical: 2 };

/**
 * Reduce the active tickets to the load metrics that drive alerting.
 *
 * @param {Array} activeTickets — tickets currently open/in-progress on the board
 * @param {number} nowMs
 * @param {Object} cfg — needs `ticketDelayMin`
 * @returns {{ activeCount, delayedCount, oldestAgeMin, backlogItems, offenders }}
 */
export function computeKitchenLoad(activeTickets, nowMs, cfg) {
  const tickets = Array.isArray(activeTickets) ? activeTickets : [];
  const delayMin = cfg?.ticketDelayMin ?? 15;
  let backlogItems = 0;
  let oldestAgeMin = 0;
  const perTicket = [];

  for (const t of tickets) {
    const items = t.items || [];
    const checked = new Set(t.checkedItems || []);
    const pendingIdx = items.map((_, i) => i).filter(i => !checked.has(i));
    backlogItems += pendingIdx.length;

    const ageMin = computeActiveElapsed({
      itemFireTimes: t.itemFireTimes,
      checkedItems: t.checkedItems || [],
      itemCheckedMs: t.itemCheckedMs || {},
      nowMs,
    });
    if (ageMin > oldestAgeMin) oldestAgeMin = ageMin;

    const label = t.ticketName || t.customerName || ('#' + (t.shortId || (t.id || '').slice(-4) || '?'));
    perTicket.push({
      id: t.id,
      label,
      ageMin,
      pending: pendingIdx.length,
      delayed: ageMin >= delayMin,
      items: pendingIdx.map(i => items[i]).filter(Boolean),
    });
  }

  return {
    activeCount: tickets.length,
    delayedCount: perTicket.filter(t => t.delayed).length,
    oldestAgeMin,
    backlogItems,
    // Tickets worth naming in a message: only those with pending work, oldest first.
    offenders: perTicket.filter(t => t.pending > 0).sort((a, b) => b.ageMin - a.ageMin),
  };
}

/**
 * Aggregate-overload test: the kitchen is collectively overwhelmed, not merely
 * slow on one order. True when ANY rule in `t` holds:
 *   • comboDelayed delayed tickets AND comboBacklog unmade items, OR
 *   • delayedAlone delayed tickets, OR
 *   • backlogAlone unmade items.
 */
function isOverloaded(m, t) {
  return (
    (m.delayedCount >= t.comboDelayed && m.backlogItems >= t.comboBacklog) ||
    m.delayedCount >= t.delayedAlone ||
    m.backlogItems >= t.backlogAlone
  );
}

/**
 * Effective level WITH hysteresis. Two states: 'ok' and 'critical'. Entering
 * critical uses the (stricter) `enter` rules; staying critical uses the (looser)
 * `clear` rules, creating a deadband so it can't flap.
 */
export function effectiveLevel(prevLevel, m, cfg) {
  if (prevLevel === 'critical') return isOverloaded(m, cfg.clear) ? 'critical' : 'ok';
  return isOverloaded(m, cfg.enter) ? 'critical' : 'ok';
}

function seed(metrics, cfg, nowMs) {
  const level = effectiveLevel('ok', metrics, cfg);
  return {
    action: 'none',
    state: {
      level,
      incidentId: level === 'ok' ? null : nowMs,
      notified: false,
      lastNotifyMs: nowMs,
      candidateLevel: null,
      candidateSince: null,
    },
  };
}

/**
 * Advance the alert state machine one tick.
 *
 * @param {Object|null} prev — previous state (null on first eval / after reset)
 * @param {Object} metrics   — from computeKitchenLoad
 * @param {Object} cfg       — KDS_LOAD_ALERT_CONFIG
 * @param {number} nowMs
 * @returns {{ action: 'none'|'escalate'|'heartbeat'|'recovered', state: Object }}
 */
export function nextAlertState(prev, metrics, cfg, nowMs) {
  // Seed silently on first evaluation (or after a reset).
  if (!prev) return seed(metrics, cfg, nowMs);

  const state = { ...prev };
  const target = effectiveLevel(state.level, metrics, cfg);

  // ── Transition with a sustain ("for:") debounce ──
  if (target !== state.level) {
    const goingUp = LEVELS[target] > LEVELS[state.level];
    const required = goingUp ? cfg.sustainUpMs : cfg.sustainDownMs;

    // (Re)start the candidate timer whenever the pending target changes.
    if (state.candidateLevel !== target) {
      state.candidateLevel = target;
      state.candidateSince = nowMs;
    }

    if (nowMs - state.candidateSince >= required) {
      const prevLevel = state.level;
      state.level = target;
      state.candidateLevel = null;
      state.candidateSince = null;

      if (LEVELS[target] > LEVELS[prevLevel]) {
        // Escalation ok → critical: start a fresh incident and page.
        state.incidentId = nowMs;
        state.notified = true;
        state.lastNotifyMs = nowMs;
        return { action: 'escalate', state };
      }
      // De-escalation critical → ok: one "caught up" iff we actually paged.
      const wasNotified = state.notified;
      state.incidentId = null;
      state.notified = false;
      return { action: wasNotified ? 'recovered' : 'none', state };
    }

    // Still waiting out the sustain window — no commit, and suppress heartbeat
    // while a downward change is pending so we don't say "still behind" as it
    // recovers.
    return { action: 'none', state };
  }

  // No pending transition.
  state.candidateLevel = null;
  state.candidateSince = null;

  // ── Renotify / heartbeat while CRITICAL ──
  // Also re-notifies an incident that was seeded silently (e.g. opened mid-rush).
  if (state.level === 'critical' && nowMs - state.lastNotifyMs >= cfg.heartbeatMs) {
    state.notified = true;
    state.lastNotifyMs = nowMs;
    return { action: 'heartbeat', state };
  }

  return { action: 'none', state };
}

// ── Cross-device dedup ────────────────────────────────────────────────────────
// Every tab/device runs its own state machine and would otherwise each send.
// These give a STABLE key per logical event (independent of any per-device
// timestamp) so a shared Firestore "claim" can let only one device send.

/**
 * Stable dedup key for an alert action. Escalations are keyed by level so a
 * warning→critical worsening still notifies; heartbeat/recovered are global.
 */
export function alertDedupKey(action, level) {
  if (action === 'escalate') return `kds_load_escalate_${level}`;
  if (action === 'heartbeat') return 'kds_load_heartbeat';
  if (action === 'recovered') return 'kds_load_recovered';
  return `kds_load_${action}`;
}

/** Dedup window (ms) for an action, from config. */
export function dedupWindowMs(action, cfg) {
  const d = cfg?.dedup || {};
  if (action === 'escalate') return d.escalateMs ?? 150 * 1000;
  if (action === 'heartbeat') return d.heartbeatMs ?? 9 * 60 * 1000;
  if (action === 'recovered') return d.recoveredMs ?? 150 * 1000;
  return 60 * 1000;
}

/**
 * Is the shared slot free to claim? A sliding window: free if never claimed or
 * the previous claim is older than the window.
 */
export function isSlotFree(existingClaimMs, nowMs, windowMs) {
  if (existingClaimMs == null) return true;
  return nowMs - existingClaimMs >= windowMs;
}

/** "2× Veg Fried Rice, Tea, Biryani +2 more" */
function summarizeItems(items, max = 3) {
  const names = (items || [])
    .map(it => {
      const q = Number(it.qty) || 1;
      return q > 1 ? `${q}× ${it.name}` : it.name;
    })
    .filter(Boolean);
  if (names.length === 0) return '';
  if (names.length <= max) return names.join(', ');
  return names.slice(0, max).join(', ') + ` +${names.length - max} more`;
}

/**
 * Build the Telegram message for an alert action.
 *
 * @param {string} action — 'escalate' | 'heartbeat' | 'recovered'
 * @param {string} level  — committed level at send time
 * @param {Object} m      — metrics from computeKitchenLoad
 * @param {Object} [ctx]  — { incidentMinutes }
 * @returns {{ title:string, body:string, severity:string }}
 */
export function formatKitchenAlert(action, level, m, { incidentMinutes = 0 } = {}) {
  if (action === 'recovered') {
    return {
      severity: 'info',
      title: '✅ Kitchen caught up',
      body: `Longest wait now ${m.oldestAgeMin}m · ${m.activeCount} active · ${m.delayedCount} delayed`,
    };
  }

  const stat = `${m.delayedCount} delayed · oldest ${m.oldestAgeMin}m · ${m.backlogItems} items · ${m.activeCount} active`;
  const offenderLines = m.offenders.slice(0, 6).map(t => {
    const items = summarizeItems(t.items);
    return `• ${t.label} — ${t.ageMin}m${items ? ' — ' + items : ''}`;
  });

  // Only critical pages, so escalations are always overload alerts.
  const title = action === 'heartbeat'
    ? `🔴 Still slammed — ${incidentMinutes}m`
    : '🔴 Kitchen overloaded — needs hands';

  return {
    severity: 'critical',
    title,
    body: [stat, '', ...offenderLines].join('\n'),
  };
}
